import { useMemo } from 'react';
import { useAppSelector } from '../../redux/ReduxHooks';
import { selectIsProgramActive } from '../../redux/RobotSlice';
import { ApplicationActions } from '../../utils/constants';
import { isDashboardLayoutLocked } from './dashboardLayoutLock';

export type DashboardViewMode = (typeof ApplicationActions.DashboardViewMode)[keyof typeof ApplicationActions.DashboardViewMode];

export type DashboardCapabilitiesInputs = {
  dashboardViewMode: DashboardViewMode;
  programState: string;
  isProgramActive: boolean;
  isReadOnlyProject: boolean;
};

export type DashboardCapabilities = {
  isHistoric: boolean;
  isLive: boolean;
  isLayoutLocked: boolean;
  isReadOnlyProject: boolean;
  /**
   * GridStack enable/disable: live + writable (program may still be running).
   * When true, existing widgets on the canvas may still be moved and resized during a program run;
   * only adding widgets from the drawer and editing widget data selection are blocked.
   */
  canInteractWithGrid: boolean;
  /** Add widgets from the drawer; edit widget data selection and countdown duration. */
  canEditDashboardLayout: boolean;
  canDeleteWidgets: boolean;
  canEditWidgetDataSelection: boolean;
  canPersistWidgetStyle: boolean;
  canPersistWidgetDataSelection: boolean;
};

/** Pure helper for unit tests and non-React consumers. */
export function getDashboardCapabilities(inputs: DashboardCapabilitiesInputs): DashboardCapabilities {
  const { dashboardViewMode, programState, isProgramActive, isReadOnlyProject } = inputs;
  const isHistoric = dashboardViewMode === ApplicationActions.DashboardViewMode.HISTORIC;
  const isLive = !isHistoric;
  const isLayoutLocked = isDashboardLayoutLocked({ programState, isProgramActive });
  const canInteractWithGrid = isLive && !isReadOnlyProject;
  const canEditDashboardLayout = canInteractWithGrid && !isLayoutLocked;

  return {
    isHistoric,
    isLive,
    isLayoutLocked,
    isReadOnlyProject,
    canInteractWithGrid,
    canEditDashboardLayout,
    canDeleteWidgets: canInteractWithGrid,
    canEditWidgetDataSelection: canEditDashboardLayout,
    canPersistWidgetStyle: canInteractWithGrid,
    canPersistWidgetDataSelection: canEditDashboardLayout,
  };
}

/** Dashboard editability derived from view mode, program state, and project read-only flag. */
export function useDashboardCapabilities(): DashboardCapabilities {
  const dashboardViewMode = useAppSelector((state) => state.application.dashboardViewMode);
  const programState = useAppSelector((state) => state.project.programState);
  const isProgramActive = useAppSelector(selectIsProgramActive);
  const isReadOnlyProject = Boolean(useAppSelector((state) => state.project.loadedProject?.readOnly));

  return useMemo(
    () => getDashboardCapabilities({ dashboardViewMode, programState, isProgramActive, isReadOnlyProject }),
    [dashboardViewMode, programState, isProgramActive, isReadOnlyProject]
  );
}
